import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Plus, Search, AlertTriangle, Camera, X } from 'lucide-react';
import '../components/delivery/delivery.css';
import Modal from '../components/Modal';
import { api, czDate, todayIso } from '../utils/deliveryApi';

const BUCKETS = [
  ['overdue', 'Po termínu'], ['today', 'Dnes'], ['week', 'Tento týden'], ['later', 'Později'], ['none', 'Bez termínu'],
];
function bucketOf(p) {
  if (!p.planned_date) return 'none';
  const d = String(p.planned_date).slice(0, 10);
  const today = todayIso();
  if (d < today) return 'overdue';
  if (d === today) return 'today';
  const week = new Date(); week.setDate(week.getDate() + 7);
  return d <= week.toISOString().slice(0, 10) ? 'week' : 'later';
}

const ProtocolCard = ({ p, onOpen }) => {
  const pct = p.total_count ? Math.round((p.checked_count / p.total_count) * 100) : 0;
  return (
    <button type="button" className="dlv-card" onClick={() => onOpen(p.id)}
      style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', padding: '16px 18px', font: 'inherit', color: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontFamily: 'Saira, sans-serif', fontSize: 17 }}>{p.header?.customer_name || '—'}</strong>
        <span className="dlv-tag" style={{ background: '#111' }}>{p.header?.model || p.model_type}</span>
        <span style={{ color: '#6b6b6b' }}>SN {p.header?.serial_number || p.serial_number}</span>
        <span style={{ marginLeft: 'auto', color: '#6b6b6b', fontSize: 13 }}>{p.status === 'closed' ? `Uzavřeno ${czDate(p.closed_at)}` : `Termín ${czDate(p.planned_date)}`}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#eee', overflow: 'hidden' }} aria-label={`Hotovo ${pct} %`}>
          <div style={{ width: `${pct}%`, height: '100%', background: p.status === 'closed' ? '#2e7d32' : '#e3141b' }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{p.checked_count}/{p.total_count}</span>
        {p.issue_count > 0 && <span style={{ color: '#e3141b', fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center' }}><AlertTriangle size={14} /> {p.issue_count}</span>}
        {p.photo_count > 0 && <span style={{ color: '#6b6b6b', fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center' }}><Camera size={14} /> {p.photo_count}</span>}
      </div>
    </button>
  );
};

const NewProtocolModal = ({ onClose, onCreated }) => {
  const [machines, setMachines] = useState([]);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    fetch('/api/machines', { credentials: 'include' }).then((r) => r.json())
      .then((j) => setMachines((j.data || []).filter((m) => m.status !== 'nahled'))).catch((e) => setError(e.message));
  }, []);
  const list = machines.filter((m) => `${m.customer_name} ${m.serial_number} ${m.model_type}`.toLowerCase().includes(q.toLowerCase())).slice(0, 50);
  const start = async (m) => {
    setError('');
    try { const d = await api('/', { method: 'POST', body: { machine_id: m.id } }); onCreated(d.id); } catch (e) {
      if (e.status === 409 && e.data?.id) onCreated(e.data.id); else setError(e.message);
    }
  };
  return (
    <Modal show onClose={onClose}>
      <div className="dlv dlv-card" style={{ width: 'min(560px, calc(100vw - 32px))', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 className="dlv-section-head__title" style={{ fontSize: 18 }}>Nový odvoz – vyberte trenažér</h2>
          <button type="button" className="dlv-iconbtn" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Zavřít"><X size={16} /></button>
        </div>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 14, color: '#999' }} />
          <input className="dlv-input" style={{ paddingLeft: 36 }} autoFocus placeholder="Zákazník, sériové číslo, model…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {error && <p role="alert" style={{ color: '#b71c1c' }}>{error}</p>}
        {list.map((m) => (
          <button key={m.id} type="button" onClick={() => start(m)} style={{ display: 'flex', width: '100%', gap: 10, alignItems: 'center', background: 'none', border: 'none', borderBottom: '1px solid #eee', padding: '12px 4px', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
            <strong style={{ flex: 1 }}>{m.customer_name || 'Bez zákazníka'}</strong>
            <span>{m.model_type}</span><span style={{ color: '#6b6b6b' }}>SN {m.serial_number}</span>
          </button>
        ))}
        {list.length === 0 && <p className="dlv-hint">Nic nenalezeno.</p>}
      </div>
    </Modal>
  );
};

// Overview of delivery protocols: open ones grouped by planned date, closed ones in their own tab
const DeliveryProtocols = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState('draft');
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { api('/').then(setRows).catch((e) => setError(e.message)); }, []);
  const open = (id) => navigate(`/odvozy/${id}`);
  const drafts = useMemo(() => (rows || []).filter((r) => r.status === 'draft'), [rows]);
  const closed = useMemo(() => (rows || []).filter((r) => r.status === 'closed'), [rows]);

  return (
    <div className="dlv" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div className="dlv-hero" style={{ marginBottom: 16 }}>
        <div className="dlv-hero__kicker">Checklist · Odvoz a předání</div>
        <h1 className="dlv-hero__title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}><ClipboardCheck /> Odvozy a předání trenažérů</h1>
        <div className="dlv-hero__meta"><span>Rozpracované <strong>{drafts.length}</strong></span><span>Uzavřené <strong>{closed.length}</strong></span></div>
        <div className="dlv-hero__badge"><button type="button" className="dlv-btn dlv-btn--primary" onClick={() => setShowNew(true)}><Plus size={18} /> Nový odvoz</button></div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }} role="tablist">
        {[['draft', `Rozpracované (${drafts.length})`], ['closed', `Uzavřené (${closed.length})`]].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`dlv-btn ${tab === k ? 'dlv-btn--dark' : ''}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {error && <p role="alert" style={{ color: '#b71c1c' }}>{error}</p>}
      {!rows ? <p className="dlv-hint">Načítám…</p> : tab === 'draft' ? (
        drafts.length === 0 ? <p className="dlv-hint">Žádný rozpracovaný odvoz. Nový založíte tlačítkem „Nový odvoz“ nebo v detailu trenažéru.</p> :
          BUCKETS.map(([key, label]) => {
            const items = drafts.filter((p) => bucketOf(p) === key);
            if (!items.length) return null;
            return (
              <section key={key} style={{ marginBottom: 20 }}>
                <h2 className="dlv-label" style={{ fontSize: 12, color: key === 'overdue' ? '#e3141b' : undefined }}>{label} ({items.length})</h2>
                <div style={{ display: 'grid', gap: 10 }}>{items.map((p) => <ProtocolCard key={p.id} p={p} onOpen={open} />)}</div>
              </section>
            );
          })
      ) : (
        closed.length === 0 ? <p className="dlv-hint">Zatím žádný uzavřený protokol.</p> :
          <div style={{ display: 'grid', gap: 10 }}>{closed.map((p) => <ProtocolCard key={p.id} p={p} onOpen={open} />)}</div>
      )}

      {showNew && <NewProtocolModal onClose={() => setShowNew(false)} onCreated={open} />}
    </div>
  );
};

export default DeliveryProtocols;
